import { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../App";
import { Sidebar } from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { toast } from "sonner";
import { User, Lock, Eye, EyeOff, CheckCircle2, Mail, Building2, Menu } from "lucide-react";

export default function MonComptePage() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password);
    
    return {
      isValid: minLength && hasUppercase && hasNumber && hasSpecial,
      minLength,
      hasUppercase,
      hasNumber,
      hasSpecial
    };
  };

  const passwordValidation = validatePassword(formData.new_password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error("Les nouveaux mots de passe ne correspondent pas");
      return;
    }

    if (!passwordValidation.isValid) {
      toast.error("Le mot de passe ne respecte pas les critères de sécurité");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      }, getAuthHeader());
      
      toast.success("Mot de passe modifié avec succès");
      setFormData({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification du mot de passe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="hidden lg:block"><Sidebar /></div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar />
        </SheetContent>
      </Sheet>
      
      <main className="flex-1">
        <header className="bg-white border-b border-gray-100 px-4 lg:px-8 py-4 lg:py-6 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileMenuOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg lg:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Mon compte
              </h1>
              <p className="text-gray-500 text-sm hidden sm:block">Gérez vos informations personnelles et votre mot de passe</p>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
        <div className="max-w-2xl mx-auto">

          {/* User Info Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-[#00A3E0]" />
                Informations personnelles
              </CardTitle>
              <CardDescription>Vos informations de compte</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Prénom</Label>
                  <p className="font-medium text-gray-900">{user?.first_name}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Nom</Label>
                  <p className="font-medium text-gray-900">{user?.last_name}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <Label className="text-xs text-gray-500">Email</Label>
                  </div>
                  <p className="font-medium text-gray-900">{user?.email}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <Label className="text-xs text-gray-500">Établissement</Label>
                  </div>
                  <p className="font-medium text-gray-900">{user?.concession || "Non renseigné"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-[#00A3E0]" />
                Modifier mon mot de passe
              </CardTitle>
              <CardDescription>Changez votre mot de passe de connexion</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Mot de passe actuel</Label>
                  <div className="relative">
                    <Input
                      id="current_password"
                      name="current_password"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.current_password}
                      onChange={handleChange}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_password">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="new_password"
                      name="new_password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.new_password}
                      onChange={handleChange}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {/* Password requirements */}
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1">
                    <p className="text-xs font-medium text-gray-600 mb-2">Critères de sécurité :</p>
                    <div className={`text-xs flex items-center gap-2 ${passwordValidation.minLength ? 'text-green-600' : 'text-gray-400'}`}>
                      <span>{passwordValidation.minLength ? '✓' : '○'}</span>
                      <span>Minimum 8 caractères</span>
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${passwordValidation.hasUppercase ? 'text-green-600' : 'text-gray-400'}`}>
                      <span>{passwordValidation.hasUppercase ? '✓' : '○'}</span>
                      <span>Une majuscule</span>
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                      <span>{passwordValidation.hasNumber ? '✓' : '○'}</span>
                      <span>Un chiffre</span>
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${passwordValidation.hasSpecial ? 'text-green-600' : 'text-gray-400'}`}>
                      <span>{passwordValidation.hasSpecial ? '✓' : '○'}</span>
                      <span>Un caractère spécial (!@#$%...)</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="confirm_password"
                      name="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#00A3E0] hover:bg-[#0090c7]"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Modification...
                    </div>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Modifier mon mot de passe
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
        </div>
      </main>
    </div>
  );
}
